import type { Vec3 } from '@rcs/domain';

/** 区間種別（ビルダーが制御点に付けるもの）。 */
export type BuilderSegment = 'normal' | 'lift' | 'launch' | 'brake' | 'station';

/** ビルダーが出力する制御点。 */
export interface BuiltPoint {
  readonly position: Vec3;
  /** バンク角 [rad]（物理仕様 2.2 節の向き） */
  readonly bank: number;
  /** この制御点から次の制御点までの区間種別 */
  readonly segment: BuilderSegment;
  /** 区間のパラメータ（省略は既定値。発進加速の `targetSpeed`・`maxAcceleration`、ブレーキの `maxDeceleration`・`targetSpeed` など） */
  readonly params?: Readonly<Record<string, number>>;
}

/** 各命令に共通のオプション。 */
export interface PieceOptions {
  /** 区間種別（既定：通常） */
  readonly segment?: BuilderSegment;
  /** 区間のパラメータ（`segment` と組み合わせる。省略は既定値） */
  readonly params?: Readonly<Record<string, number>>;
}

/** 曲線の命令のオプション。 */
export interface CurveOptions extends PieceOptions {
  /** 緩和曲線（曲率を 0 から最大まで直線的に変える部分）の長さ [m]（既定：半径の 0.6 倍） */
  readonly transition?: number;
  /** 曲率が最大のところのバンク角 [rad]（旋回のみ。緩和曲線では曲率に比例させる） */
  readonly bank?: number;
}

const STANDARD_GRAVITY = 9.80665;
/** 内部で軌跡を進める刻み [m] */
const STEP = 0.05;

/**
 * 「亀」の要領でコースの形を組み立てる（サンプルコース・新しいコースの雛形用）。
 *
 * 進行方向は、水平の向き（ヨー：+Y 軸まわり、0 で +X）と傾き（ピッチ：上向きが正）で表す。
 * 曲線は、実際のコースと同じく緩和曲線（曲率を 0 から直線的に上げる）で円弧に入り、円弧を出る。
 * 曲率が急に変わらないので、上下 G・横 G が跳ねない。上下 G の目安は `1 + v²/(r g)`（谷）、
 * `1 − v²/(r g)`（丘の頂上）。
 *
 * 軌跡を細かく進め、`spacing` ごとに制御点を出す。区間種別が変わるところでも制御点を出す。
 */
export class TrackBuilder {
  private x: number;
  private y: number;
  private z: number;
  private yaw = 0;
  private pitch = 0;
  private bank = 0;
  private sinceLast = 0;
  private segment: BuilderSegment = 'normal';
  private params: Readonly<Record<string, number>> | undefined;
  private readonly points: BuiltPoint[] = [];

  /**
   * @param start 始点
   * @param spacing 制御点の間隔 [m]
   * @param initialPitchDegrees 始点での傾き [度]（上向きが正）
   */
  constructor(
    start: Vec3,
    private spacing = 5,
    initialPitchDegrees = 0,
  ) {
    [this.x, this.y, this.z] = start;
    this.pitch = (initialPitchDegrees * Math.PI) / 180;
    this.points.push({ position: [...start], bank: 0, segment: 'normal' });
  }

  /**
   * これから出す制御点の間隔 [m] を変える。急な曲線（遅いところの頂上など）だけ細かくして、
   * 曲線をなめらかに通す（制御点の間でサンプル軌道の曲率が波打たないようにする）。
   */
  withSpacing(spacing: number): this {
    if (!(spacing > 0)) throw new RangeError('制御点の間隔は正にしてください');
    this.spacing = spacing;
    return this;
  }

  /** 直線を進む。 */
  straight(length: number, options: PieceOptions = {}): this {
    this.begin(options);
    this.bank = 0;
    for (let travelled = 0; travelled < length - 1e-9; travelled += STEP) {
      this.advance(Math.min(STEP, length - travelled));
    }
    return this;
  }

  /**
   * 縦の曲線：最小半径 `radius` で、傾きを `toDegrees` まで変える（上向きが正）。
   * 谷（下向き → 上向き）と、丘の頂上（上向き → 下向き）の両方に使う。
   */
  pitchTo(radius: number, toDegrees: number, options: CurveOptions = {}): this {
    const target = (toDegrees * Math.PI) / 180;
    this.curve(target - this.pitch, radius, options, (delta) => {
      this.pitch += delta;
    });
    this.pitch = target;
    return this;
  }

  /**
   * 水平の旋回：最小半径 `radius`（水平面）で、向きを `degrees` だけ変える（正は +Y 軸まわり）。
   * 傾き（ピッチ）は保つので、傾いた状態で回ればヘリックスになる。
   * `bank` は旋回の内側へ傾ける大きさ [rad]（符号は旋回の向きから決める）。正の `degrees` は左旋回。
   */
  turn(radius: number, degrees: number, options: CurveOptions = {}): this {
    const angle = (degrees * Math.PI) / 180;
    // ヨーが正（+Y 軸まわり）の旋回は左旋回。バンク角は「上方が右へ傾く向き」が正なので、
    // 内側（左）へ傾けるには負にする
    const bankMax = -(options.bank ?? 0) * Math.sign(angle);
    this.curve(
      angle,
      radius,
      options,
      (delta) => {
        this.yaw += delta / Math.cos(this.pitch);
      },
      (ratio) => {
        this.bank = bankMax * ratio;
      },
    );
    this.bank = 0;
    return this;
  }

  /**
   * 上下 G を指定して作る垂直ループ（クロソイド型・涙滴型のループの考え方）。
   * 進入速度 `v0`（水平・最下点）から、力学的エネルギー保存の速度 `v² = v0² − 2 g h` で、
   * 上下 G が目標 `n(θ)` になる曲率 `κ = (n(θ) − cos θ)·g / v²` で傾きを 360° 回す。
   * 目標は、最下点 `gBottom` から頂上 `gTop` へ `(1 + cos θ)/2` で移る（頂上ほど半径が小さい涙滴形）。
   * 入口・出口は、G を 1 から徐々に上げ下げする（緩和曲線）。
   * 自己交差を避けるため、1 周で `sideShift` だけ横（進行方向の右）へずらす。
   */
  loop(
    v0: number,
    gBottom: number,
    gTop: number,
    sideShift: number,
    options: PieceOptions = {},
  ): this {
    if (Math.abs(this.pitch) > 1e-9) throw new RangeError('ループは水平から始めてください');
    this.begin(options);
    const baseY = this.y;
    const total = 2 * Math.PI;
    const easeAngle = Math.PI / 4;
    const sideX = Math.sin(this.yaw);
    const sideZ = Math.cos(this.yaw);
    let turned = 0;
    for (let guard = 0; turned < total; guard += 1) {
      if (guard > 1_000_000) throw new RangeError('ループを閉じられません');
      const v2 = v0 * v0 - 2 * STANDARD_GRAVITY * (this.y - baseY);
      if (!(v2 > 1)) throw new RangeError('ループの頂上へ届きません');
      // 入口・出口で G を緩める（角度について対称にして、入口と出口の高さをそろえる）
      const ease = Math.max(0.08, Math.min(1, turned / easeAngle, (total - turned) / easeAngle));
      const target = gTop + ((gBottom - gTop) * (1 + Math.cos(turned))) / 2;
      const g = 1 + (target - 1) * ease;
      const kappa = Math.max(0, ((g - Math.cos(turned)) * STANDARD_GRAVITY) / v2);
      const delta = Math.min(kappa * STEP, total - turned);
      turned += delta;
      this.pitch = turned;
      this.advance(STEP);
      const shift = (sideShift * delta) / total;
      this.x += sideX * shift;
      this.z += sideZ * shift;
    }
    this.pitch = 0;
    return this;
  }

  /**
   * 上下 G を指定する縦の曲線：いまの速度 `speed`（ここでの速さ）から、力学的エネルギー保存の速度
   * `v² = speed² − 2 g Δh` で、上下 G が `targetG` になる曲率で、傾きを `toDegrees` まで変える。
   * 丘の頂上（トップハット・キャメルバック）は 0 G 付近、谷は 3〜4 G など。速いところほど半径が大きく、
   * 遅いところ（頂上）ほど半径が小さくなる。入口・出口では、G を、まっすぐ進むときの値から徐々に変える。
   *
   * 上下 G は `n = cos θ + κ v² / g`（谷・上向きへ曲がる）、`n = cos θ − κ v² / g`（頂上・下向きへ曲がる）。
   * 指定の G にできない向き（頂上で `targetG > cos θ` など）のところは、曲率の下限 `1 / maxRadius` で曲がる。
   *
   * @param maxRadius 曲率がほぼ 0 になるところの、半径の上限 [m]（既定 400）
   * @param minRadius 半径の下限 [m]（既定なし。遅いところで、指定の G にすると急すぎる曲線を緩める）
   * @param ease 入口・出口で G を徐々に変える角度 [度]（既定 15。回す角度の 1/4 以下。0 で変えない）
   * @param easeOut 出口だけの角度 [度]（既定は `ease` と同じ。0 なら、出口で G を戻さず次へつなぐ）
   */
  pitchByG(
    speed: number,
    targetG: number,
    toDegrees: number,
    options: PieceOptions & {
      readonly maxRadius?: number;
      readonly minRadius?: number;
      readonly ease?: number;
      readonly easeOut?: number;
    } = {},
  ): this {
    this.begin(options);
    const target = (toDegrees * Math.PI) / 180;
    const total = target - this.pitch;
    if (total === 0) return this;
    const sign = Math.sign(total);
    const baseY = this.y;
    const start = this.pitch;
    const easeAngle = Math.min(((options.ease ?? 15) * Math.PI) / 180, Math.abs(total) / 4);
    const easeOutAngle = Math.min(
      ((options.easeOut ?? options.ease ?? 15) * Math.PI) / 180,
      Math.abs(total) / 4,
    );
    const minKappa = 1 / (options.maxRadius ?? 400);
    const maxKappa = options.minRadius === undefined ? Infinity : 1 / options.minRadius;
    let turned = 0;
    for (let guard = 0; Math.abs(turned) < Math.abs(total) - 1e-9; guard += 1) {
      if (guard > 1_000_000) throw new RangeError('曲線を作れません');
      const v2 = speed * speed - 2 * STANDARD_GRAVITY * (this.y - baseY);
      if (!(v2 > 1)) throw new RangeError('曲線の途中で止まってしまいます（速度が足りません）');
      const done = Math.abs(turned);
      // 入口・出口で G を徐々に変える（`ease: 0` なら変えない）
      const easeIn = easeAngle <= 0 ? 1 : done / easeAngle;
      const easeOut = easeOutAngle <= 0 ? 1 : (Math.abs(total) - done) / easeOutAngle;
      const ease = Math.max(0.05, Math.min(1, easeIn, easeOut));
      const straightG = Math.cos(this.pitch);
      const g = straightG + (targetG - straightG) * ease;
      // 上向きへ曲がる（sign > 0）なら n = cos θ + κ v²/g、下向きなら n = cos θ − κ v²/g
      const kappa = Math.min(
        maxKappa,
        Math.max(minKappa, (sign * (g - straightG) * STANDARD_GRAVITY) / v2),
      );
      const delta = sign * Math.min(kappa * STEP, Math.abs(total) - done);
      turned += delta;
      this.pitch = start + turned;
      this.advance(STEP);
    }
    this.pitch = target;
    return this;
  }

  /** いまの制御点の間隔 [m]。 */
  get currentSpacing(): number {
    return this.spacing;
  }

  /** 現在位置の高さ [m]。 */
  get height(): number {
    return this.y;
  }

  /** 終点を加えて、制御点の列を返す。 */
  build(): BuiltPoint[] {
    this.emit(true);
    return this.points;
  }

  /**
   * 緩和曲線つきの曲線。曲率は台形（0 → 最大 → 0）。角度 `angle`（符号つき）を回し切る。
   * 回る量が少なく台形にならないときは、三角形（最大曲率は 1/radius 以下）にする。
   */
  private curve(
    angle: number,
    radius: number,
    options: CurveOptions,
    turnBy: (delta: number) => void,
    onRatio?: (ratio: number) => void,
  ): void {
    this.begin(options);
    if (angle === 0) return;
    const kappaMax = 1 / radius;
    let ramp = Math.max(0, options.transition ?? radius * 0.6);
    let body = Math.abs(angle) / kappaMax - ramp;
    if (body < 0) {
      ramp = Math.abs(angle) / kappaMax;
      body = 0;
    }
    const length = 2 * ramp + body;
    const sign = Math.sign(angle);
    let turned = 0;
    for (let travelled = 0; travelled < length - 1e-9; travelled += STEP) {
      const step = Math.min(STEP, length - travelled);
      const middle = travelled + step / 2;
      const ratio = ramp === 0 ? 1 : Math.min(1, middle / ramp, (length - middle) / ramp);
      onRatio?.(ratio);
      const delta = sign * Math.min(kappaMax * ratio * step, Math.abs(angle) - Math.abs(turned));
      turned += delta;
      turnBy(delta);
      this.advance(step);
    }
    // 端数（数値誤差）を回し切る
    turnBy(angle - turned);
  }

  /** 命令の始まり：区間種別が変わるなら、ここに制御点を出す。 */
  private begin(options: PieceOptions): void {
    const segment = options.segment ?? 'normal';
    const params = options.params;
    if (segment === this.segment && sameParams(params, this.params)) return;
    this.segment = segment;
    this.params = params;
    const last = this.points[this.points.length - 1];
    if (last && this.sinceLast < 1) {
      // 直前の制御点に近すぎるときは、その点の区間種別を変える
      this.points[this.points.length - 1] = {
        ...last,
        segment,
        ...(params === undefined ? {} : { params }),
      };
      return;
    }
    this.emit();
  }

  private advance(step: number): void {
    const horizontal = Math.cos(this.pitch) * step;
    this.x += Math.cos(this.yaw) * horizontal;
    this.z -= Math.sin(this.yaw) * horizontal;
    this.y += Math.sin(this.pitch) * step;
    this.sinceLast += step;
    if (this.sinceLast >= this.spacing - 1e-9) this.emit();
  }

  private emit(final = false): void {
    if (final && this.sinceLast < 1e-6) return;
    this.points.push({
      position: [round(this.x), round(this.y), round(this.z)],
      bank: round(this.bank),
      segment: this.segment,
      ...(this.params === undefined ? {} : { params: this.params }),
    });
    this.sinceLast = 0;
  }
}

function sameParams(
  a: Readonly<Record<string, number>> | undefined,
  b: Readonly<Record<string, number>> | undefined,
): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
}

function round(value: number): number {
  // −0 は 0 にする（保存データの JSON では −0 を表せず、読み込むと 0 になるため）
  return Math.round(value * 1000) / 1000 || 0;
}
